import { useEffect, useState } from "react";
import axios from "axios";
import config from "./config";

/**
 * Link preview card — the Facebook-style unfurl under a post that contains a
 * link, and the live preview in the Create post popup while it's being written.
 *
 * The data comes from flexiapi's membership-link-preview, which reads the page's
 * Open Graph tags server-side and answers with a `kind`:
 *   "page"  — title / description / image of an ordinary page
 *   "image" — the link IS an image (…/photo.jpg, picsum, a CDN URL): show it
 *   null    — nothing to show; `blocked` says the site refused us (Cloudflare
 *             and similar only let Facebook's own network read their pages)
 *
 * Shared by MembershipShell and CommunityFeed so the two feeds can't drift.
 */

// One lookup per URL per page load: the composer, the published post and every
// re-render share it.
const cache = new Map();

function fetchPreview(url) {
  if (!cache.has(url)) {
    cache.set(
      url,
      axios
        .post(`${config.COMMUNITY_API_URL}/membership-link-preview`, { url })
        .then((res) => (res.data?.error === 0 ? res.data : { kind: null, blocked: false }))
        .catch(() => {
          cache.delete(url); // a network blip shouldn't stick for the session
          return { kind: null, blocked: false };
        })
    );
  }
  return cache.get(url);
}

/** Older cached answers predate `kind` — infer it the way the server now does. */
function kindOf(d) {
  if (!d) return null;
  if (d.kind !== undefined) return d.kind;
  return d.title || d.image ? "page" : null;
}

function hostOf(url, fallback = "") {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return fallback;
  }
}

function originOf(url) {
  try {
    return new URL(url).origin;
  } catch {
    return "";
  }
}

const GlobeIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
  </svg>
);
const LinkOffIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 13.5a3.5 3.5 0 005 0l3-3a3.5 3.5 0 00-5-5l-1 1M13.5 10.5a3.5 3.5 0 00-5 0l-3 3a3.5 3.5 0 005 5l1-1M4 4l16 16" />
  </svg>
);
const ExternalIcon = ({ className = "h-3.5 w-3.5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" />
  </svg>
);

/** Site's own favicon, beside its name; quietly dropped if it doesn't load. */
function SiteLine({ url, site }) {
  const [iconOk, setIconOk] = useState(true);
  const origin = originOf(url);
  return (
    <p className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
      {origin && iconOk ? (
        <img src={`${origin}/favicon.ico`} alt="" className="h-3.5 w-3.5 shrink-0 rounded-sm" onError={() => setIconOk(false)} />
      ) : (
        <GlobeIcon className="h-3.5 w-3.5 shrink-0" />
      )}
      <span className="truncate">{site}</span>
    </p>
  );
}

function Skeleton() {
  return (
    <div className="mt-3 flex overflow-hidden rounded-2xl border border-gray-200 bg-gray-50" aria-hidden="true">
      <div className="h-28 w-28 shrink-0 animate-pulse bg-gray-200 sm:w-40" />
      <div className="flex-1 space-y-2.5 p-4">
        <div className="h-2.5 w-20 animate-pulse rounded-full bg-gray-200" />
        <div className="h-3.5 w-4/5 animate-pulse rounded-full bg-gray-200" />
        <div className="h-2.5 w-3/5 animate-pulse rounded-full bg-gray-200" />
      </div>
    </div>
  );
}

/**
 * @param url      the link found in the post text
 * @param compose  true inside the Create post popup: waits for typing to settle,
 *                 shows a loading skeleton, and explains when there's no preview
 * @param onRemove composer only: shows an ✕ on the card ("Remove preview")
 * @param removed  composer only: the author removed it — show the undo row
 * @param onRestore composer only: undo for `removed`
 * @param small    under a comment or reply: a narrower card, page links always
 *                 thumbnail-beside-text, a shorter image
 */
export default function LinkPreview({ url, compose = false, onRemove, removed = false, onRestore, small = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgState, setImgState] = useState("loading"); // loading | wide | narrow | error

  useEffect(() => {
    let active = true;
    setData(null);
    setLoading(true);
    setImgState("loading");
    // While typing, every keystroke is a new "URL" — only look up the one the
    // member stops on.
    const t = setTimeout(
      () => {
        fetchPreview(url).then((d) => {
          if (!active) return;
          setData(d);
          setLoading(false);
        });
      },
      compose ? 450 : 0
    );
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [url, compose]);

  if (removed) {
    return (
      <div className="mt-3 flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-3.5 py-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-gray-200/70 text-gray-500">
          <LinkOffIcon />
        </span>
        <p className="min-w-0 flex-1 text-[12.5px] leading-snug text-gray-500">
          <span className="font-semibold text-gray-700">Link preview removed.</span> Your post will show just the link.
        </p>
        {onRestore && (
          <button
            type="button"
            onClick={onRestore}
            className="shrink-0 rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-gray-600 transition hover:bg-gray-200 hover:text-gray-900"
          >
            Undo
          </button>
        )}
      </div>
    );
  }

  if (loading) return compose ? <Skeleton /> : null;

  // The ✕ sits beside the card, not inside it — the card is a link, and a button
  // nested in an <a> would open the site on click.
  const withRemove = (card) =>
    compose && onRemove ? (
      <div className="relative">
        {card}
        <span className="group/rm absolute right-2.5 top-[calc(0.75rem+10px)] z-10">
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove link preview"
            className="grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white shadow-lg ring-1 ring-white/25 backdrop-blur-md transition hover:scale-105 hover:bg-black/80"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span
            role="tooltip"
            className="pointer-events-none absolute right-0 top-full mt-2 w-56 translate-y-1 rounded-xl bg-[#15171d] px-3 py-2 ring-1 ring-white/10 text-left opacity-0 shadow-xl transition duration-150 group-hover/rm:translate-y-0 group-hover/rm:opacity-100 group-focus-within/rm:translate-y-0 group-focus-within/rm:opacity-100"
          >
            <span className="block text-[12px] font-semibold text-white">Remove preview</span>
            <span className="mt-0.5 block text-[11.5px] leading-snug text-white/70">
              Your post will show just the link, without the image or title.
            </span>
          </span>
        </span>
      </div>
    ) : (
      card
    );

  const kind = kindOf(data);
  const host = hostOf(url, data?.site || "");
  const site = (data?.site && !/\./.test(data.site) ? data.site : host) || host;

  /* ---------------------------------------------------------- the link is an image */
  if (kind === "image" && imgState !== "error") {
    return withRemove(
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={`group block overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 transition hover:shadow-md ${
          small ? "mt-1.5 max-w-sm" : "mt-3"
        }`}
      >
        {/* A portrait or small image leaves empty space at the sides. Fill it
            with a soft, blurred copy of the same picture (Instagram-style)
            rather than a fixed colour — a dark band looked wrong in the light
            theme and a light one in the dark theme. */}
        <div className="relative flex justify-center overflow-hidden bg-gray-100">
          <img
            src={data.image}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full scale-125 object-cover opacity-60 blur-2xl saturate-150"
          />
          <img
            src={data.image}
            alt=""
            className={`relative w-auto max-w-full object-contain shadow-[0_8px_30px_-12px_rgba(0,0,0,0.45)] ${
              small ? "max-h-48" : "max-h-[28rem]"
            }`}
            onLoad={() => setImgState("wide")}
            onError={() => setImgState("error")}
          />
        </div>
        <div className="flex items-center justify-between gap-3 px-4 py-2.5">
          <SiteLine url={url} site={host} />
          <span className="inline-flex shrink-0 items-center gap-1 text-[11.5px] font-semibold text-gray-400 transition group-hover:text-gray-700">
            Open image <ExternalIcon />
          </span>
        </div>
      </a>
    );
  }

  /* ------------------------------------------------------------- an ordinary page */
  if (kind === "page" && (data.title || (data.image && imgState !== "error"))) {
    const showImg = data.image && imgState !== "error";
    // A big landscape og:image gets the full-width hero; anything else (square,
    // small, still loading) sits beside the text the way Facebook does it.
    const hero = !small && showImg && imgState === "wide";
    return withRemove(
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={`group flex overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 transition hover:-translate-y-px hover:shadow-md ${
          hero ? "flex-col" : "flex-row"
        } ${small ? "mt-1.5 max-w-sm" : "mt-3"}`}
      >
        {showImg && (
          <div className={`relative shrink-0 overflow-hidden bg-gray-200 ${hero ? "w-full" : small ? "w-24" : "w-28 sm:w-44"}`}>
            <img
              src={data.image}
              alt=""
              className={`w-full object-cover transition duration-500 group-hover:scale-[1.03] ${hero ? "max-h-72" : small ? "h-full min-h-[5.5rem]" : "h-full min-h-[7rem]"}`}
              onLoad={(e) => {
                const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                setImgState(w >= 500 && w / h >= 1.5 ? "wide" : "narrow");
              }}
              onError={() => setImgState("error")}
            />
          </div>
        )}
        <div className={`flex min-w-0 flex-1 flex-col justify-center gap-1 ${small ? "px-3 py-2.5" : "px-4 py-3"}`}>
          <SiteLine url={url} site={site} />
          {data.title && (
            <p className={`line-clamp-2 font-semibold leading-snug text-gray-900 ${small ? "text-[13.5px]" : "text-[15px]"}`}>{data.title}</p>
          )}
          {data.description && (
            <p className={`text-gray-500 ${small ? "line-clamp-1 text-[12px]" : "line-clamp-2 text-[13px] leading-relaxed"}`}>{data.description}</p>
          )}
        </div>
      </a>
    );
  }

  /* ---------------------------------------------------------------- no preview */
  if (compose) {
    return (
      <div className="mt-3 flex items-start gap-3 rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gray-200/70 text-gray-500">
          <GlobeIcon className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-gray-800">
            {data?.blocked ? "Preview not supported for this site" : "No preview available for this link"}
          </p>
          <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500">
            {data?.blocked
              ? `${host} doesn't allow other sites to show its pages or images. Your post will still include the link.`
              : "This page doesn't share a title or image. Your post will still include the link."}
          </p>
        </div>
      </div>
    );
  }

  // In the feed: a tidy link card rather than nothing, so the post still reads
  // as "shared a link".
  let path = "";
  try {
    const u = new URL(url);
    path = decodeURIComponent(u.pathname).replace(/\/+$/, "");
  } catch {
    /* keep empty */
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`group flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 transition hover:shadow-md ${
        small ? "mt-1.5 max-w-sm px-3 py-2.5" : "mt-3 px-4 py-3"
      }`}
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gray-200/70 text-gray-500">
        <GlobeIcon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-semibold text-gray-900">{host}</span>
        {path && <span className="block truncate text-[12.5px] text-gray-500">{path}</span>}
      </span>
      <span className="shrink-0 text-gray-400 transition group-hover:text-gray-700">
        <ExternalIcon className="h-4 w-4" />
      </span>
    </a>
  );
}

/**
 * WhatsApp-style preview for a comment / reply field: one compact strip joined
 * to the TOP of the input (the input squares off its top corners to meet it),
 * thumbnail on the left, title and site on the right. Nothing renders while the
 * link has no preview — a comment field has no room for the explanation.
 */
export function LinkPreviewStrip({ url, primary = "#16a34a" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgOk, setImgOk] = useState(true);

  useEffect(() => {
    let active = true;
    setData(null);
    setLoading(true);
    setImgOk(true);
    const t = setTimeout(() => {
      fetchPreview(url).then((d) => {
        if (!active) return;
        setData(d);
        setLoading(false);
      });
    }, 450);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [url]);

  const shell = "lp-strip flex items-center gap-3 overflow-hidden rounded-t-2xl border border-b-0 border-gray-200 bg-gray-50 py-2 pl-2 pr-3";
  const accent = <span className="w-1 self-stretch shrink-0 rounded-full" style={{ backgroundColor: primary }} aria-hidden="true" />;

  if (loading) {
    return (
      <div className={shell} aria-hidden="true">
        {accent}
        <div className="h-11 w-11 shrink-0 animate-pulse rounded-lg bg-gray-200" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-3/5 animate-pulse rounded-full bg-gray-200" />
          <div className="h-2.5 w-1/3 animate-pulse rounded-full bg-gray-200" />
        </div>
      </div>
    );
  }

  const kind = kindOf(data);
  if (kind !== "image" && !(kind === "page" && (data.title || data.image))) return null;

  const host = hostOf(url, data?.site || "");
  const site = (data?.site && !/\./.test(data.site) ? data.site : host) || host;
  const title = kind === "image" ? "Photo" : data.title || site;
  const sub = kind === "image" ? host : data.description || site;

  return (
    <div className={shell}>
      {accent}
      <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg bg-gray-200 text-gray-500">
        {data.image && imgOk ? (
          <img src={data.image} alt="" className="h-full w-full object-cover" onError={() => setImgOk(false)} />
        ) : (
          <GlobeIcon className="h-5 w-5" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold text-gray-900">{title}</span>
        <span className="block truncate text-[11.5px] text-gray-500">{sub}</span>
      </span>
    </div>
  );
}
